<script lang="ts">
	import PlaceExtras from '$lib/components/city/feature/PlaceExtras.svelte';
	import TextImageSidesStructure, {
		ItemDescription,
		ItemNativeName,
		ItemQuickFacts,
		ItemTitle,
		ItemViewPrimary
	} from '$lib/components/city/features/TextImageSidesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { TIME_OF_DAY_METAS, type TimeOfDayMeta } from '$lib/features/_other/timeOfDay.enum';
	import {
		CITY_FEATURE_TYPE_COLOURS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import type { Market } from '$lib/features/market/market.type';
	import { MARKET_CATEGORY_ICONS, MARKET_CATEGORY_LABELS } from '$lib/features/market/marketCategory.enum';
	import { FULL_SITE_URL } from '$lib/statics';
	import {
		buildExampleItemsPhrase,
		closestByLength,
		findFirstAvailableImage,
		sortShortestToLongest
	} from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconCalendarToday from '~icons/mdi/calendar-today';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.MARKET;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const items = $derived<Market[]>(output.items);
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(items.map((item) => item.feature.englishName));
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: ImageDetails | undefined = findFirstAvailableImage(items);

		interface Limits {
			exampleItems: number;
		}
		function examplesLimits(limits: Limits): { exampleItems: string } {
			return {
				exampleItems: buildExampleItemsPhrase({
					names: orderedNames,
					totalItems: orderedNames.length,
					limit: limits.exampleItems
				})
			};
		}
		const examples = {
			large: examplesLimits({ exampleItems: 4 }),
			mid: examplesLimits({ exampleItems: 3 }),
			small: examplesLimits({ exampleItems: 2 })
		};
		const description: string = closestByLength([
			`Explore markets in ${location.city.label}, ${examples.large.exampleItems} — where and when to shop, eat, and explore.`,
			`Explore markets in ${location.city.label}, ${examples.mid.exampleItems} — where and when to shop, eat, and explore.`,
			`Explore markets in ${location.city.label}, ${examples.small.exampleItems} — where and when to shop, eat, and explore.`
		]);

		return {
			title: `${location.city.label} Markets & Bazaars: ${shortestName} & More`,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdTouristAttractionsCollection = $derived.by(() => {
		const json = data.output.jsonLdTouristAttractionsCollection;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdTouristAttractionsCollection]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`The Best Markets in ${location.city.label}`}
	description={`Dive into the local culture. Whether you're hunting for vintage treasures, fresh street food, or handmade crafts, these are the must-visit markets—and the perfect times to experience them.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		coordinates: items.flatMap((item) => item.primary?.place?.coordinates ?? item.feature.place?.coordinates ?? []),
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<ul class="flex flex-col gap-12 lg:gap-16">
		{#each items as item, i (item.feature.id)}
			<li>
				{@render ItemRow(item, i === 0)}
			</li>
		{/each}
	</ul>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet ItemRow(item: Market, isFirstEager: boolean)}
	{const images = $derived<ImageDetails[]>(item.feature.images.length > 0 ? item.feature.images : item.primary?.images || [])}
	{const { label, Icon } = $derived<TimeOfDayMeta>(TIME_OF_DAY_METAS[item.visitOnTimeOfDay])}

	<article
		id={item.feature.slug}
		class="flex scroll-mt-28 flex-col gap-8 rounded-box border border-base-content/10 bg-base-100 p-6 shadow-sm transition-all hover:border-base-content/20 md:p-8"
	>
		<TextImageSidesStructure englishName={item.feature.englishName} {images} eagerLoadImages={isFirstEager}>
			{#snippet TextContentSnippet()}
				<div class="flex flex-col gap-1">
					{@render ItemTitle({ englishName: item.feature.englishName })}
					{@render ItemNativeName({
						nativeName: item.feature.nativeName,
						language: output.countryLanguage
					})}
				</div>
				{@render ItemQuickFacts({
					facts: [
						{
							text: MARKET_CATEGORY_LABELS[item.category],
							title: 'Market type',
							Icon: MARKET_CATEGORY_ICONS[item.category]
						},
						{
							text: `Visit on ${item.visitOnDay}`,
							title: 'Recommended visit day',
							Icon: IconCalendarToday
						},
						{
							text: `Visit in the ${label}`,
							title: 'Recommended visit time',
							Icon: Icon
						}
					]
				})}
				<div class="divider my-0 opacity-50"></div>
				{@render ItemDescription({ text: item.feature.description })}
				{@render ItemViewPrimary({ primary: item.primary, location })}
			{/snippet}
		</TextImageSidesStructure>

		<div class="border-t border-base-content/10 pt-6">
			<PlaceExtras
				name={item.feature.englishName}
				place={item.primary?.place ?? item.feature.place}
				videos={item.feature.videos}
				typeMapPoints={output.typeMapPoints}
			/>
		</div>
	</article>
{/snippet}
